import { supabase, isConfigured } from "./supabase";
import { SEED_PAINTINGS, SEED_SHOP } from "../data";

/* ============================================================
   Data access for the gallery.

   Two modes:
   - Supabase configured  -> everything lives in the database, shared by
                             every visitor, and only the artist can write.
   - not configured       -> local preview: data stays in this browser
                             (same behaviour the site had before).
   ============================================================ */

/* ---------------- local preview fallback ---------------- */
const LK = {
  paintings: "kg:paintings:v2", orders: "kg:orders", messages: "kg:messages",
  testimonials: "kg:testimonials", news: "kg:newsletter", settings: "kg:settings",
  shop: "kg:shop:v1",
};
const lsGet = (k, fb) => {
  try { const raw = window.localStorage.getItem(k); return raw ? JSON.parse(raw) : fb; }
  catch { return fb; }
};
const lsSet = (k, v) => { try { window.localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* full or blocked */ } };

/* ---------------- helpers ---------------- */
/* Every network call goes through this. If Supabase is unreachable — bad key,
   project paused, visitor offline — we hand back a value instead of throwing,
   so the site still renders rather than sitting on a loading screen. */
async function safe(run, fallback) {
  try { return await run(); }
  catch (e) { console.error("supabase", e); return fallback; }
}

const withDate = (row) => ({ ...row, date: row.created_at });
const uid = () => Math.random().toString(36).slice(2, 9);

/* ---------------- auth ---------------- */
const localPaintings = () => {
  const saved = lsGet(LK.paintings, null);
  return Array.isArray(saved) && saved.length > 0 ? saved : SEED_PAINTINGS;
};

const localShop = () => {
  const saved = lsGet(LK.shop, null);
  return Array.isArray(saved) && saved.length > 0 ? saved : SEED_SHOP;
};

export async function getSession() {
  if (!isConfigured) return null;
  return safe(async () => {
    const { data } = await supabase.auth.getSession();
    return data.session || null;
  }, null);
}

/* True only if this signed-in user is listed in the admins table.
   The check is a database read protected by RLS, so it cannot be faked
   from the browser — and even if it were, every write would still fail. */
export async function checkIsAdmin(session) {
  if (!isConfigured || !session) return false;
  return safe(async () => {
    const { data, error } = await supabase
      .from("admins").select("user_id").eq("user_id", session.user.id).maybeSingle();
    if (error) return false;
    return Boolean(data);
  }, false);
}

export async function signIn(email, password) {
  if (!isConfigured) return { error: { message: "Supabase is not connected yet." } };
  return safe(
    () => supabase.auth.signInWithPassword({ email, password }),
    { error: { message: "Couldn't reach the server. Check your connection and try again." } }
  );
}

export async function signOut() {
  if (isConfigured) await safe(() => supabase.auth.signOut(), null);
}

export function onAuthChange(cb) {
  if (!isConfigured) return () => {};
  const { data } = supabase.auth.onAuthStateChange((_e, session) => cb(session));
  return () => data.subscription.unsubscribe();
}

/* ---------------- public reads (every visitor) ---------------- */
export async function loadPublic(seedPaintings, seedSettings) {
  if (!isConfigured) {
    return {
      paintings: localPaintings(),
      shopItems: localShop(),
      testimonials: lsGet(LK.testimonials, []),
      settings: { ...seedSettings, ...lsGet(LK.settings, {}) },
    };
  }
  const empty = { data: null, error: null };
  const loadPaintings = async () => {
    /* The artist's own arrangement (sort_order). New paintings have no position yet, so they show first.
       If the sort_order column hasn't been added yet, fall back to newest-first so the gallery still loads. */
    const arranged = await safe(
      () => supabase.from("paintings").select("*")
        .order("sort_order", { ascending: true, nullsFirst: true }).order("created_at", { ascending: false }), empty);
    if (!arranged.error) return arranged;
    return safe(() => supabase.from("paintings").select("*").order("created_at", { ascending: false }), empty);
  };
  /* Prints and friends arrived later than the gallery, so the table may not
     exist yet on a project where 008_shop.sql has not been run. A missing table
     must not take the whole site down with it — an empty list just means the
     Prints page shows nothing. */
  const loadShop = () => safe(
    () => supabase.from("shop_items").select("*")
      .order("sort_order", { ascending: true, nullsFirst: true })
      .order("created_at", { ascending: false }), empty);

  const [p, t, s, sh] = await Promise.all([
    loadPaintings(),
    safe(() => supabase.from("testimonials").select("*").order("created_at", { ascending: true }), empty),
    safe(() => supabase.from("settings").select("*").eq("id", 1).maybeSingle(), empty),
    loadShop(),
  ]);
  return {
    paintings: p.data || [],
    shopItems: sh.data || [],
    testimonials: t.data || [],
    settings: { ...seedSettings, ...(s.data || {}) },
    error: p.error || t.error || s.error || null,
  };
}

/* ---------------- artist-only reads ---------------- */
export async function loadStudio() {
  if (!isConfigured) {
    return { orders: lsGet(LK.orders, []), messages: lsGet(LK.messages, []), news: lsGet(LK.news, []) };
  }
  const empty = { data: null, error: null };
  const [o, m, n] = await Promise.all([
    safe(() => supabase.from("orders").select("*").order("created_at", { ascending: false }), empty),
    safe(() => supabase.from("messages").select("*").order("created_at", { ascending: false }), empty),
    safe(() => supabase.from("newsletter").select("*").order("created_at", { ascending: false }), empty),
  ]);
  return {
    orders: (o.data || []).map(withDate),
    messages: (m.data || []).map(withDate),
    news: (n.data || []).map(withDate),
  };
}

/* ---------------- paintings ---------------- */
export async function savePainting(art) {
  if (!isConfigured) {
    const list = localPaintings();
    const row = { ...art, id: art.id || uid() };
    const next = list.some((p) => p.id === row.id) ? list.map((p) => (p.id === row.id ? row : p)) : [row, ...list];
    lsSet(LK.paintings, next);
    return { data: row, error: null };
  }
  const row = {
    title: art.title, price: Number(art.price) || 0, year: art.year, medium: art.medium,
    dims: art.dims, category: art.category, status: art.status,
    featured: !!art.featured, description: art.description, images: art.images,
  };
  if (art.id) row.id = art.id;
  return safe(
    () => supabase.from("paintings").upsert(row).select().single(),
    { data: null, error: { message: "couldn't reach the server" } }
  );
}

export async function patchPainting(id, patch) {
  if (!isConfigured) {
    const next = localPaintings().map((p) => (p.id === id ? { ...p, ...patch } : p));
    lsSet(LK.paintings, next);
    return { error: null };
  }
  return safe(
    () => supabase.from("paintings").update(patch).eq("id", id),
    { error: { message: "couldn't reach the server" } }
  );
}

/* Save a new gallery order. `ids` is the full list of painting ids, first to last. */
export async function reorderPaintings(ids) {
  if (!isConfigured) {
    const byId = new Map(localPaintings().map((p) => [p.id, p]));
    lsSet(LK.paintings, ids.map((id) => byId.get(id)).filter(Boolean));
    return { error: null };
  }
  const results = await Promise.all(ids.map((id, i) =>
    safe(() => supabase.from("paintings").update({ sort_order: i + 1 }).eq("id", id),
      { error: { message: "couldn't reach the server" } })));
  const failed = results.find((r) => r && r.error);
  return { error: failed ? failed.error : null };
}

export async function deletePainting(id) {
  if (!isConfigured) {
    lsSet(LK.paintings, localPaintings().filter((p) => p.id !== id));
    return { error: null };
  }
  return safe(
    () => supabase.from("paintings").delete().eq("id", id),
    { error: { message: "couldn't reach the server" } }
  );
}

/* Upload a painting photo to Storage and return its permanent public URL.
   In local preview mode there is nowhere to upload to, so the caller keeps
   using an inline data URL instead. */
export async function uploadPaintingImage(blob) {
  if (!isConfigured) return { url: null, error: { message: "not configured" } };
  const name = `${Date.now()}-${uid()}.jpg`;
  const { error } = await safe(
    () => supabase.storage.from("paintings")
      .upload(name, blob, { contentType: "image/jpeg", cacheControl: "31536000" }),
    { error: { message: "couldn't reach the server" } }
  );
  if (error) return { url: null, error };
  const { data } = supabase.storage.from("paintings").getPublicUrl(name);
  return { url: data.publicUrl, error: null };
}

/* ---------------- prints, stickers & bookmarks ---------------- */
/* Deliberately mirrors the painting functions above, so there is only one
   pattern to learn. The difference is that nothing here has a status: these are
   reproductions and never sell out. */

const cleanVariants = (variants) => (Array.isArray(variants) ? variants : [])
  .map((v) => ({ id: String(v.id || "").trim(), label: String(v.label || "").trim(), price: Number(v.price) || 0 }))
  .filter((v) => v.id && v.label && v.price > 0);

export async function saveShopItem(item) {
  if (!isConfigured) {
    const list = localShop();
    const row = { ...item, id: item.id || uid(), variants: cleanVariants(item.variants) };
    const next = list.some((x) => x.id === row.id) ? list.map((x) => (x.id === row.id ? row : x)) : [row, ...list];
    lsSet(LK.shop, next);
    return { data: row, error: null };
  }
  const row = {
    kind: item.kind, title: item.title, description: item.description,
    images: item.images, variants: cleanVariants(item.variants), active: item.active !== false,
  };
  if (item.id) row.id = item.id;
  return safe(
    () => supabase.from("shop_items").upsert(row).select().single(),
    { data: null, error: { message: "couldn't reach the server" } }
  );
}

export async function patchShopItem(id, patch) {
  if (!isConfigured) {
    lsSet(LK.shop, localShop().map((x) => (x.id === id ? { ...x, ...patch } : x)));
    return { error: null };
  }
  return safe(
    () => supabase.from("shop_items").update(patch).eq("id", id),
    { error: { message: "couldn't reach the server" } }
  );
}

export async function deleteShopItem(id) {
  if (!isConfigured) {
    lsSet(LK.shop, localShop().filter((x) => x.id !== id));
    return { error: null };
  }
  return safe(
    () => supabase.from("shop_items").delete().eq("id", id),
    { error: { message: "couldn't reach the server" } }
  );
}

/* ---------------- orders ---------------- */
export async function createOrder(order) {
  const row = { ...order, ref: "KG-" + Date.now().toString(36).toUpperCase().slice(-6), status: "New" };
  if (!isConfigured) {
    const withTime = { ...row, date: new Date().toISOString() };
    lsSet(LK.orders, [withTime, ...lsGet(LK.orders, [])]);
    return { data: withTime, error: null };
  }
  const { error } = await safe(
    () => supabase.from("orders").insert({
      ref: row.ref, name: row.name, email: row.email, address: row.address,
      note: row.note, mode: row.mode, items: row.items, total: row.total, status: "New",
    }),
    { error: { message: "couldn't reach the server" } }
  );
  return { data: { ...row, date: new Date().toISOString() }, error };
}

export async function patchOrder(ref, patch) {
  if (!isConfigured) {
    lsSet(LK.orders, lsGet(LK.orders, []).map((o) => (o.ref === ref ? { ...o, ...patch } : o)));
    return { error: null };
  }
  return safe(
    () => supabase.from("orders").update(patch).eq("ref", ref),
    { error: { message: "couldn't reach the server" } }
  );
}

/* ---------------- exchange rates (display only) ---------------- */
/* Asks our own server for today's rates. Anything short of a clear answer means
   the site keeps showing Canadian dollars — see src/lib/currency.jsx. Nothing
   here affects what anyone is charged. */
const NO_RATES = { base: "CAD", rates: {} };
const WANTED = ["USD", "EUR", "GBP", "AED"];

/* Live site: our own /api/rates, which caches a day's rates at Cloudflare's
   edge so the upstream service sees one request per region per day. */
async function ratesFromServer() {
  const res = await fetch("/api/rates", { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`rates: server replied ${res.status}`);
  const body = await res.json();
  if (!body || !body.rates || Object.keys(body.rates).length === 0) throw new Error("rates: server had none");
  return body;
}

/* `npm start` runs the React dev server alone — Cloudflare's functions aren't
   there, so /api/rates doesn't exist and every currency would sit greyed out,
   making the switcher impossible to try locally. In development only, ask the
   rate service straight from the browser instead. Never in production: there it
   would be one request per visitor rather than one per day, and the cached
   endpoint exists precisely to avoid that. */
async function ratesDirect() {
  const res = await fetch("https://open.er-api.com/v6/latest/CAD", { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`rates: direct replied ${res.status}`);
  const body = await res.json();
  const all = (body && body.result === "success" && body.rates) || null;
  if (!all) throw new Error("rates: direct sent none");
  const rates = {};
  for (const code of WANTED) {
    const n = all[code];
    if (typeof n === "number" && Number.isFinite(n) && n > 0.001 && n < 1000) rates[code] = n;
  }
  if (Object.keys(rates).length === 0) throw new Error("rates: direct had none of ours");
  return { base: "CAD", rates, source: "browser (development only)" };
}

export async function loadRates() {
  try {
    return await ratesFromServer();
  } catch (e) {
    if (process.env.NODE_ENV === "development") {
      try {
        const r = await ratesDirect();
        console.info("rates: /api/rates is not running locally, fetched them in the browser instead");
        return r;
      } catch (e2) {
        console.warn(e2.message);
      }
    }
    /* No rates: the site shows Canadian dollars rather than guessing. */
    return NO_RATES;
  }
}

/* ---------------- card payment ---------------- */
/* Ask our server whether it can take a card payment. Anything other than a
   clear "yes" — no server (npm start on your Mac), keys missing, network
   trouble — counts as no, and the site offers the purchase inquiry instead. */
export async function checkCardPayments() {
  try {
    const res = await fetch("/api/checkout", { headers: { Accept: "application/json" } });
    if (!res.ok) return false;
    const body = await res.json();
    return body && body.enabled === true;
  } catch {
    return false;
  }
}

/* Hands the cart to our own server function, which reads the real prices from
   the database and asks Stripe for a payment page. Nothing about the amount is
   decided here in the browser. */
export async function startCardCheckout({ items, name, email, note }) {
  try {
    const res = await fetch("/api/checkout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items, name, email, note }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || !body.url) {
      /* Our server always explains itself. A reply without an explanation means
         something in between answered instead — the HTTP status says what. */
      const fallback = `Couldn't start the payment. Please try again. (code H${res.status}${body.url === undefined && res.ok ? "-nourl" : ""})`;
      return { url: null, error: body.error || fallback, soldIds: body.soldIds || [] };
    }
    return { url: body.url, ref: body.ref, error: null, soldIds: [] };
  } catch (e) {
    console.error("checkout", e);
    return { url: null, error: "Couldn't reach the payment service. Please check your connection.", soldIds: [] };
  }
}

/* Coming back from Stripe: ask our server to check with Stripe whether the
   payment really went through, and to finish the order if it did. */
export async function confirmCardPayment(sessionId) {
  try {
    const res = await fetch("/api/confirm", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId }),
    });
    const body = await res.json().catch(() => ({}));
    return body && body.status ? body : { status: "unknown" };
  } catch {
    return { status: "unknown" };
  }
}

/* ---------------- messages ---------------- */
/* Goes to our own server, which saves it AND emails Karima, so a message
   can't sit unseen in the Studio. */
export async function createMessage(f) {
  if (!isConfigured) {
    lsSet(LK.messages, [{ ...f, date: new Date().toISOString() }, ...lsGet(LK.messages, [])]);
    return { error: null };
  }
  try {
    const res = await fetch("/api/message", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: f.name, email: f.email, topic: f.topic, message: f.message }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { error: { message: body.error || "Your message couldn't be sent." } };
    return { error: null };
  } catch (e) {
    console.error("message", e);
    return { error: { message: "Couldn't reach the server. Please check your connection." } };
  }
}

/* ---------------- testimonials ---------------- */
export async function createTestimonial(name, text) {
  if (!isConfigured) {
    const row = { id: uid(), name, text };
    lsSet(LK.testimonials, [...lsGet(LK.testimonials, []), row]);
    return { data: row, error: null };
  }
  return safe(
    () => supabase.from("testimonials").insert({ name, text }).select().single(),
    { data: null, error: { message: "couldn't reach the server" } }
  );
}

export async function deleteTestimonial(id) {
  if (!isConfigured) {
    lsSet(LK.testimonials, lsGet(LK.testimonials, []).filter((t) => t.id !== id));
    return { error: null };
  }
  return safe(
    () => supabase.from("testimonials").delete().eq("id", id),
    { error: { message: "couldn't reach the server" } }
  );
}

/* ---------------- newsletter ---------------- */
export async function subscribeEmail(email) {
  if (!isConfigured) {
    const list = lsGet(LK.news, []);
    if (list.some((n) => n.email.toLowerCase() === email.toLowerCase())) return { already: true, error: null };
    lsSet(LK.news, [...list, { email, date: new Date().toISOString() }]);
    return { already: false, error: null };
  }
  const { error } = await safe(
    () => supabase.from("newsletter").insert({ email: email.toLowerCase() }),
    { error: { message: "couldn't reach the server" } }
  );
  /* 23505 = unique violation, i.e. already subscribed. Not an error worth showing. */
  if (error && error.code === "23505") return { already: true, error: null };
  return { already: false, error };
}

/* ---------------- settings ---------------- */
export async function saveSettings(s) {
  if (!isConfigured) { lsSet(LK.settings, s); return { error: null }; }
  return safe(
    () => supabase.from("settings").upsert({
      id: 1, instagram: s.instagram, youtube: s.youtube, email: s.email, statement: s.statement,
      /* Only sent once the artist has used the home-page image setting, so other
         settings keep saving even before supabase/004_hero_image.sql has been run. */
      ...(s.hero_image !== undefined ? { hero_image: s.hero_image || null } : {}),
      ...(s.about_image !== undefined ? { about_image: s.about_image || null } : {}),
    }),
    { error: { message: "couldn't reach the server" } }
  );
}
